import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { toast } from 'react-toastify';

import { MODULE_NAMES } from '../../../Constants/modules';
import { CANCEL_TEXT } from '../../../Constants/strings';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import {
  createRegion,
  regionListRequest,
  updateRegion,
} from '../../../Api/request/regionList';
import FormDialog from '../../../Components/Modal/modal';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { regionListTableConfig } from './RegionListTableConfig';
import './RegionList.scss';

const INITIAL_REGION_FORM = {
  regionName: '',
};

const getAxiosResultMessage = (result) => (
  result?.data?.response?.data?.message
  || result?.data?.response?.data?.externalMessage
  || result?.data?.message
  || ''
);

const isSuccessfulAxiosResult = (result) => (
  Number.isInteger(result?.status) && result.status >= 200 && result.status < 300
);

const renderRegionModal = ({
  dialogMode,
  regionForm,
  fieldErrors,
  isSubmitDisabled,
  handleRegionFormChange,
  handleCloseRegionDialog,
  handleSubmitRegion,
}) => createPortal(
  <div className="region-list-modal-backdrop">
    <form className="region-list-modal" onSubmit={handleSubmitRegion}>
      <div className="region-list-modal__header">
        {dialogMode === 'edit' ? 'Edit Region' : 'Add Region'}
      </div>
      <div className="region-list-modal__body">
        <div className="region-list-modal__field">
          <label htmlFor="region-name">
            Region Name<span className="required-asterisk">*</span>
          </label>
          <input
            id="region-name"
            name="regionName"
            type="text"
            placeholder="Enter Region Name"
            value={regionForm.regionName}
            onChange={handleRegionFormChange}
            className={fieldErrors.regionName ? 'is-error' : ''}
          />
        </div>
      </div>
      <div className="region-list-modal__footer">
        <button
          type="button"
          className="region-list-modal__btn region-list-modal__btn--ghost"
          onClick={handleCloseRegionDialog}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="region-list-modal__btn region-list-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {dialogMode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

// Added hasActionAccess to dynamically filter columns
const buildRegionConfig = ({ userProfile, showAddNew, total, onAddClick, hasActionAccess }) => {
  const dynamicTableConfig = regionListTableConfig.filter(col => {
    if (col.field === 'action' && !hasActionAccess) {
      return false;
    }
    return true;
  });

  return {
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {
        emailId: userProfile?.email || '',
        permissions: userProfile?.permissions || {},
        roleDetails: userProfile?.roleDetails || [],
        employeeId: userProfile?.employeeId || '',
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },
      handlers: {
        handleAddIconClick: onAddClick,
      },
      toolBarSettings: {
        showAddNew,
        showFilterOption: false,
        showAppliedFilters: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Region List',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: dynamicTableConfig, // Use filtered config
      loaderSkeleton: {
        rows: 8,
        bodyCells: 1,
        columns: dynamicTableConfig.length, // Update column count for skeleton loader
      },
      paginationConfig: {
        page: 1,
        limit: Math.max(total || 0, 1000),
        total,
        rowsPerPageOptions: [5, 10, 25, 50],
      },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: {
        desktop: 1,
        tablet: 1,
        mobile: 1,
      },
    },
  };
};

const RegionList = () => {
  const { userProfile } = useAuth();

  // Specific module targeting and Super Admin logic
  const permission = userProfile?.permissions?.[MODULE_NAMES.MASTERS_REGION_LIST];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );

  const normalizedPermissions = useMemo(
    () => (permission || []).map((action) => String(action).toLowerCase()),
    [permission],
  );

  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;

    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
      delete: ['delete', 'remove'],
    };

    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);

  const [regions, setRegions] = useState([]);
  const [dialogState, setDialogState] = useState({
    mode: 'add',
    selectedRegion: null,
    isOpen: false,
  });
  const [regionForm, setRegionForm] = useState(INITIAL_REGION_FORM);
  const [fieldErrors, setFieldErrors] = useState({ regionName: false });
  const [isLoading, setIsLoading] = useState(false);
  const [toggleRegion, setToggleRegion] = useState(null);

  const loadRegions = useCallback(async () => {
    if (!hasAccess('read')) {
      setRegions([]);
      return;
    }
    try {
      const response = await UseAxios(regionListRequest(), setIsLoading);
      if (!isSuccessfulAxiosResult(response)) {
        setRegions([]);
        return;
      }
      const rows = response?.data?.regionList || response?.data?.data?.tableData || [];
      setRegions(rows.map((row) => ({
        ...row,
        regionId: row.regionId ?? row.region_id,
        regionName: row.regionName ?? row.region_name,
        isActive: row.isActive ?? Boolean(row.is_active),
        status: row.status || ((row.isActive ?? row.is_active) ? 'Active' : 'Inactive'),
      })));
    } catch (_error) {
      setRegions([]);
    }
  }, [hasAccess, setIsLoading]);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  const handleOpenAddRegion = useCallback(() => {
    setRegionForm(INITIAL_REGION_FORM);
    setFieldErrors({ regionName: false });
    setDialogState({ mode: 'add', selectedRegion: null, isOpen: true });
  }, []);

  const handleOpenEditRegion = useCallback((region) => {
    setRegionForm({ regionName: region.regionName || '' });
    setFieldErrors({ regionName: false });
    setDialogState({ mode: 'edit', selectedRegion: region, isOpen: true });
  }, []);

  const handleCloseRegionDialog = useCallback(() => {
    setRegionForm(INITIAL_REGION_FORM);
    setFieldErrors({ regionName: false });
    setDialogState({ mode: 'add', selectedRegion: null, isOpen: false });
  }, []);

  const handleRegionFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setRegionForm((current) => ({ ...current, [name]: value }));
    if (name === 'regionName' && value.trim()) {
      setFieldErrors({ regionName: false });
    }
  }, []);

  const isSubmitDisabled = useMemo(() => {
    if (dialogState.mode !== 'edit' || !dialogState.selectedRegion) {
      return false;
    }
    return regionForm.regionName.trim() === dialogState.selectedRegion.regionName?.trim();
  }, [dialogState.mode, dialogState.selectedRegion, regionForm.regionName]);

  const handleSubmitRegion = useCallback(async (event) => {
    event.preventDefault();
    const trimmedRegionName = regionForm.regionName.trim();
    if (!trimmedRegionName) {
      setFieldErrors({ regionName: true });
      toast.error('Please enter region name.');
      return;
    }
    const request = dialogState.mode === 'edit'
      ? updateRegion(
        dialogState.selectedRegion.regionId,
        trimmedRegionName,
        dialogState.selectedRegion.isActive ? 1 : 0,
      )
      : createRegion(trimmedRegionName);
    const response = await UseAxios(request, setIsLoading);
    if (!isSuccessfulAxiosResult(response)) {
      toast.error(getAxiosResultMessage(response) || 'Failed to save region.');
      return;
    }
    toast.success(
      response?.data?.message
        || (dialogState.mode === 'edit'
          ? 'Region updated successfully.'
          : 'Region added successfully.'),
    );
    handleCloseRegionDialog();
    loadRegions();
  }, [
    dialogState.mode,
    dialogState.selectedRegion,
    handleCloseRegionDialog,
    loadRegions,
    regionForm.regionName,
    setIsLoading,
  ]);

  const handleToggleRegion = useCallback((region) => {
    setToggleRegion(region);
  }, []);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleRegion(null);
  }, []);

  const handleConfirmToggleRegion = useCallback(async () => {
    if (!toggleRegion) return;
    const response = await UseAxios(
      updateRegion(toggleRegion.regionId, toggleRegion.regionName, toggleRegion.isActive ? 0 : 1),
      setIsLoading,
    );
    if (!isSuccessfulAxiosResult(response)) {
      toast.error(getAxiosResultMessage(response) || 'Failed to update region status.');
      return;
    }
    toast.success(
      response?.data?.message
        || `Region ${toggleRegion.isActive ? 'disabled' : 'enabled'} successfully.`,
    );
    handleCloseToggleDialog();
    loadRegions();
  }, [handleCloseToggleDialog, loadRegions, setIsLoading, toggleRegion]);

  const tableData = useMemo(() => regions.map((region, index) => ({
    ...region,
    slNo: index + 1,
    status: region.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="region-list-actions-cell" onClick={(event) => event.stopPropagation()}>
        <div className="region-list-action-cell-inner">

          {(hasAccess('update') || hasAccess('delete')) && (
            <ControlledSwitches
              checked={Boolean(region.isActive)}
              switchCss="switchCss"
              handleChange={() => handleToggleRegion(region)}
            />
          )}

          {hasAccess('update') && (
            <button
              type="button"
              className="region-list-action-btn"
              title="Edit Region"
              aria-label={`Edit ${region.regionName}`}
              onClick={() => handleOpenEditRegion(region)}
            >
              <img src={EditActionIcon} alt="" />
            </button>
          )}

        </div>
      </div>
    ),
  })), [hasAccess, handleOpenEditRegion, handleToggleRegion, regions]);

  const configuration = useMemo(() => buildRegionConfig({
    userProfile,
    showAddNew: hasAccess('create'),
    total: tableData.length,
    onAddClick: handleOpenAddRegion,
    hasActionAccess: hasAccess('update') || hasAccess('delete'), // Check passed to builder
  }), [handleOpenAddRegion, hasAccess, tableData.length, userProfile]);

  if (!hasAccess('read')) {
    return <div>You do not have permission to view region list.</div>;
  }

  return (
    <div className="region-list-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialogState.isOpen && renderRegionModal({
        dialogMode: dialogState.mode,
        regionForm,
        fieldErrors,
        isSubmitDisabled,
        handleRegionFormChange,
        handleCloseRegionDialog,
        handleSubmitRegion,
      })}
      <FormDialog
        open={Boolean(toggleRegion)}
        title={toggleRegion?.isActive ? 'Disable Region' : 'Enable Region'}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {toggleRegion?.isActive ? 'Do you want to disable this region?' : 'Do you want to enable this region?'}
            </div>
          </div>
        )}
        button1={CANCEL_TEXT}
        cancelButtonId="cancel-region-toggle"
        button2={toggleRegion?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="region-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggleRegion}
      />
    </div>
  );
};

export default RegionList;
